import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/icons';
import { Badge, EmptyState, ErrorState, LoadingRows, PageHeader } from '../components/ui';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatDateTime, includesTr } from '../lib/format';
import type { AuditEvent } from '../lib/types';

// Sunucu action filtresini TAM eşleşme ile uygular (eq); aktör kısmi (ilike) arar.
const ACTION_EXAMPLES = [
  'auth.login',
  'auth.logout',
  'auth.totp_setup',
  'ai.test',
  'ai.settings.update',
  'wa.pair',
  'wa.reconnect',
  'inbox.send',
  'ticket.create',
  'ticket.reply',
  'faq.create',
  'library.upload',
  'settings.update'
];

function resultBadge(result: AuditEvent['result']) {
  if (result === 'ok') return <Badge tone="ok">ok</Badge>;
  if (result === 'denied') return <Badge tone="danger">reddedildi</Badge>;
  return <Badge tone="warn">hata</Badge>;
}


/** S12 — Audit: GET /api/admin/audit (action/actor/result filtreleri, cursor sayfalama) + CSV. */
export default function AuditPage() {
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');

  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(
    async (cursor: string | null, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const page = await api.audit({
          action: action.trim() || undefined,
          actor: actor.trim() || undefined,
          cursor,
          limit: 50
        });
        setRows((current) => (append ? [...current, ...page.items] : page.items));
        setNextCursor(page.nextCursor);
      } catch (cause) {
        setError(cause);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [action, actor]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => void load(null, false), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const summary = useMemo(
    () => ({
      denied: rows.filter((row) => row.result === 'denied').length,
      error: rows.filter((row) => row.result === 'error').length
    }),
    [rows]
  );

  async function downloadCsv() {
    try {
      const { blob, filename } = await api.auditCsv({
        action: action.trim() || undefined,
        actor: actor.trim() || undefined,
        limit: 1000
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError(cause);
    }
  }

  return (
    <div>
      <PageHeader
        title="Audit"
        subtitle="Gönderim, guard, AI, ticket ve ayar değişikliklerinin kaydı. Kayıtlar silinemez."
        actions={
          <button type="button" className="btn btn-sm" onClick={() => void downloadCsv()} disabled={rows.length === 0}>
            <Icon name="download" size={14} />
            CSV indir
          </button>
        }
      />

      <div className="flex flex-wrap items-end gap-2 border-b border-line bg-surface px-6 py-2">
        <div className="relative w-[240px]">
          <Icon name="search" size={14} className="pointer-events-none absolute left-2 top-2 text-muted" />
          <input
            className="input pl-7"
            placeholder="Eylem (tam eşleşme: auth.login)"
            value={action}
            onChange={(event) => setAction(event.target.value)}
            aria-label="Eylem filtresi"
            list="audit-actions"
            title="Eylem adı tam eşleşmeyle aranır"
          />
          <datalist id="audit-actions">
            {ACTION_EXAMPLES.map((example) => (
              <option key={example} value={example} />
            ))}
          </datalist>
        </div>
        <div className="relative w-[200px]">
          <Icon name="user" size={14} className="pointer-events-none absolute left-2 top-2 text-muted" />
          <input
            className="input pl-7"
            placeholder="Aktör (ad/e-posta)"
            value={actor}
            onChange={(event) => setActor(event.target.value)}
            aria-label="Aktör filtresi"
            title="Aktör e-postasında kısmi arama yapar"
          />
        </div>
        <span className="hidden items-center gap-2 text-[11px] text-muted min-[1100px]:flex">
          <span>eylem: tam eşleşme</span>
          <span>·</span>
          <span>aktör: kısmi</span>
        </span>
        <span className="ml-auto flex items-center gap-2 text-[11px] text-muted">
          <span className="mono">{rows.length} kayıt</span>
          {summary.denied > 0 ? <Badge tone="danger">{summary.denied} reddedildi</Badge> : null}
          {summary.error > 0 ? <Badge tone="warn">{summary.error} hata</Badge> : null}
        </span>
      </div>

      {error ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void load(null, false)} />
      ) : loading ? (
        <LoadingRows rows={10} columns={5} />
      ) : rows.length === 0 ? (
        <EmptyState icon="list" title="Kayıt bulunamadı" hint="Filtreleri değiştirin veya arama metnini temizleyin." />
      ) : (
        <>
          <table className="table">
            <caption className="sr-only">Audit kayıtları</caption>
            <thead>
              <tr>
                <th scope="col" style={{ width: '160px' }}>Zaman</th>
                <th scope="col" style={{ width: '150px' }}>Aktör</th>
                <th scope="col" style={{ width: '190px' }}>Eylem</th>
                <th scope="col">Hedef</th>
                <th scope="col" style={{ width: '110px' }}>Sonuç</th>
                <th scope="col" style={{ width: '110px' }}>IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="mono text-[12px] text-muted">{formatDateTime(row.at)}</td>
                  <td className="max-w-[150px] truncate text-[12px]">{row.actor}</td>
                  <td className="mono text-[12px]">{row.action}</td>
                  <td className="max-w-[320px]">
                    <div className="truncate text-[12px]">{row.target}</div>
                    {row.meta ? <div className="truncate text-[11px] text-muted">{row.meta}</div> : null}
                  </td>
                  <td>{resultBadge(row.result)}</td>
                  <td className="mono text-[12px] text-muted">{row.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between px-6 py-3">
            <span className="text-[11px] text-muted">
              {rows.length} kayıt gösteriliyor. CSV, filtreye uyan tüm sayfaları indirir.
            </span>
            {nextCursor ? (
              <button type="button" className="btn btn-sm" onClick={() => void load(nextCursor, true)} disabled={loadingMore}>
                {loadingMore ? 'Yükleniyor…' : 'Daha fazla'}
              </button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}